/* eslint-disable */
import React, { useEffect, useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Link,
  IconButton,
  Box,
  Chip,
} from "@mui/material";
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import LocationSearchingIcon from "@mui/icons-material/LocationSearching";
import PlaceIcon from "@mui/icons-material/Place";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import RepeatIcon from "@mui/icons-material/Repeat";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { formatSpeed, formatTime } from "../common/util/formatter";
import ReportFilter from "./components/ReportFilter";
import { prefixString } from "../common/util/stringUtils";
import { useTranslation } from "../common/components/LocalizationProvider";
import PageLayout from "../common/components/PageLayout";
import ReportsMenu from "./components/ReportsMenu";
import usePersistedState from "../common/util/usePersistedState";
import ColumnSelect from "./components/ColumnSelect";
import { useCatch, useEffectAsync } from "../reactHelper";
import useReportStyles from "./common/useReportStyles";
import TableShimmer from "../common/components/TableShimmer";
import { useAttributePreference } from "../common/util/preferences";
import MapView from "../map/core/MapView";
import MapGeofence from "../map/MapGeofence";
import MapPositions from "../map/MapPositions";
import MapCamera from "../map/MapCamera";
import { utils, writeFileXLSX } from "xlsx";
import { nativePostMessage } from "../common/components/NativeInterface";
import { sessionActions } from "../store";
import { PrintLayout } from "./common/print";
import fetchOrThrow from "../common/util/fetchOrThrow";

const columnsArray = [
  ["eventTime", "positionFixTime"],
  ["type", "sharedType"],
  ["geofenceId", "sharedGeofence"],
  ["attributes", "commandData"],
];
const columnsMap = new Map(columnsArray);

// A stable empty object used as a fallback
const EMPTY_MAP = {}

const GeofencesReportPage = () => {
  const classes = useReportStyles();
  const t = useTranslation();
  const dispatch = useDispatch();
  const tokens = usePremiumTokens();

  // Use stable empty object fallback and shallow equal to prevent rerenders
  const devices = useSelector((state) => state?.devices?.items ?? EMPTY_MAP, shallowEqual);
  const geofences = useSelector((state) => state?.geofences?.items ?? EMPTY_MAP, shallowEqual);
  const printing = useSelector((state) => state.session.printing ?? false);

  const speedUnit = useAttributePreference("speedUnit");

  const [columns, setColumns] = usePersistedState("eventColumns", [
    "eventTime",
    "type",
    "geofenceId",
    "attributes",
  ]);
  const [eventTypes] = useState(["geofenceEnter", "geofenceExit", "geofenceStay"]);
  const [items, setItems] = useState([]);
  const [hasResults, setHasResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [position, setPosition] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Stats por geocerca
  const stats = useMemo(() => {
    if (items.length === 0) return null;

    const enters = items.filter((i) => i.type === "geofenceEnter").length;
    const exits = items.filter((i) => i.type === "geofenceExit").length;
    const stays = items.filter((i) => i.type === "geofenceStay").length;

    // Contagem por geocerca
    const byGeofence = {};
    items.forEach((item) => {
      const geoId = item.geofenceId;
      if (geoId) {
        if (!byGeofence[geoId]) {
          byGeofence[geoId] = { enter: 0, exit: 0, stay: 0 };
        }
        if (item.type === "geofenceEnter") byGeofence[geoId].enter++;
        if (item.type === "geofenceExit") byGeofence[geoId].exit++;
        if (item.type === "geofenceStay") byGeofence[geoId].stay++;
      }
    });

    return {
      total: items.length,
      enters,
      exits,
      stays,
      uniqueGeofences: Object.keys(byGeofence).length,
      byGeofence,
    };
  }, [items]);
  const selectedDeviceName = selectedDeviceId
    ? devices[selectedDeviceId]?.name || devices[selectedDeviceId]?.uniqueId || `${selectedDeviceId}`
    : undefined;

  useEffect(() => {
    const handleAfterPrint = () => {
      dispatch(sessionActions.setPrinting(false));
    };
    window.addEventListener("afterprint", handleAfterPrint);
    return () => {
      window.removeEventListener("afterprint", handleAfterPrint);
    };
  }, [dispatch]);

  useEffect(() => {
    if (printing) {
      requestAnimationFrame(() =>
        requestAnimationFrame(() => nativePostMessage(`print|${t("sharedGeofences")}.pdf`)),
      );
    }
  }, [printing, t]);

  useEffectAsync(async () => {
    if (selectedItem) {
      try {
        const response = await fetchOrThrow(`/api/positions?id=${selectedItem.positionId}`);
        const positions = await response.json();
        if (positions.length > 0) {
          setPosition(positions[0]);
        } else {
          setPosition(null);
        }
      } catch (error) {
        console.error("Error fetching position:", error);
        setPosition(null);
      }
    } else {
      setPosition(null);
    }
  }, [selectedItem]);

  const fetchEvents = async ({ deviceId, from, to }) => {
    if (!deviceId || !from || !to) {
      setItems([]);
      setHasResults(false);
      return [];
    }

    setLoading(true);
    setHasResults(false);
    try {
      const query = new URLSearchParams({ deviceId, from, to, cost: 0 });
      eventTypes.forEach((it) => query.append("type", it));
      const response = await fetchOrThrow(`/api/reports/events?${query.toString()}`, {
        headers: { Accept: "application/json" },
      });
      const data = await response.json();
      setItems(data || []);
      setHasResults((data || []).length > 0);
      return data;
    } catch (error) {
      console.error("Error fetching events:", error);
      setItems([]);
      setHasResults(false);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleShow = useCatch(async ({ deviceIds = [], from, to }) => {
    const deviceId = deviceIds[0];
    if (!deviceId || !from || !to) {
      setItems([]);
      setHasResults(false);
      return;
    }
    setSelectedDeviceId(deviceId);
    setFromDate(from);
    setToDate(to);
    await fetchEvents({ deviceId, from, to });
    setPage(0);
  });

  const handleExportReport = useCatch(async ({ deviceIds = [], from, to } = {}) => {
    const deviceId = deviceIds.length ? deviceIds[0] : selectedDeviceId;
    const effectiveFrom = from ?? fromDate;
    const effectiveTo = to ?? toDate;
    if (!deviceId || !effectiveFrom || !effectiveTo) {
      return;
    }
    setSelectedDeviceId(deviceId);
    setFromDate(effectiveFrom);
    setToDate(effectiveTo);
    if (!items.length) {
      await fetchEvents({ deviceId, from: effectiveFrom, to: effectiveTo });
    }
    const table = document.querySelector("table");
    if (!table) {
      return;
    }
    requestAnimationFrame(() =>
      writeFileXLSX(utils.table_to_book(table), `${t("sharedGeofences")}.xlsx`)
    );
  });

  const handlePrintReport = useCatch(async ({ deviceIds = [], from, to } = {}) => {
    const deviceId = deviceIds.length ? deviceIds[0] : selectedDeviceId;
    const effectiveFrom = from ?? fromDate;
    const effectiveTo = to ?? toDate;
    if (!deviceId || !effectiveFrom || !effectiveTo) {
      return;
    }
    setSelectedDeviceId(deviceId);
    setFromDate(effectiveFrom);
    setToDate(effectiveTo);
    if (!items.length) {
      await fetchEvents({ deviceId, from: effectiveFrom, to: effectiveTo });
    }
    dispatch(sessionActions.setPrinting(true));
  });

  const formatValue = (item, key) => {
    if (!item) return "";
    
    switch (key) {
      case "eventTime":
        return item[key] ? formatTime(item[key]) : "";
      case "type":
        return item[key] ? t(prefixString("event", item[key])) : "";
      case "geofenceId":
        if (item[key] > 0) {
          const geofence = geofences[item[key]];
          return geofence && geofence.name;
        }
        return null;
      case "attributes":
        if (!item.attributes || !item.type) return "";
        
        switch (item.type) {
          case "alarm":
            return item.attributes.alarm ? t(prefixString("alarm", item.attributes.alarm)) : "";
          case "deviceOverspeed":
            return item.attributes.speed !== undefined ? formatSpeed(item.attributes.speed, speedUnit, t) : "";
          case "driverChanged":
            return item.attributes.driverUniqueId || "";
          case "sendCommand":
            return item.attributes.command ? t(prefixString("command", item.attributes.command)) : "";
          case "media":
            if (item.attributes.file && devices[item.deviceId]?.uniqueId) {
              return (
                <Link
                  href={`/api/media/${devices[item.deviceId].uniqueId}/${item.attributes.file}`}
                  target="_blank"
                >
                  {item.attributes.file}
                </Link>
              );
            }
            return "";
          case "commandResult":
            return item.attributes.result || "";
          default:
            return "";
        }
      default:
        return item[key] || "";
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, items.length - page * rowsPerPage);

  if (printing) {
    return (
      <PrintLayout
        title={t("sharedGeofences")}
        deviceName={selectedDeviceName}
        from={fromDate}
        to={toDate}
        onReady={() => window.print()}
      >
        <Table className="print-table">
          <TableHead>
            <TableRow>
              {columns.map((key) => (
                <TableCell key={key}>{t(columnsMap.get(key) || key)}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id || Math.random()}>
                {columns.map((key) => (
                  <TableCell key={key}>{formatValue(item, key)}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </PrintLayout>
    );
  }

  // Conteúdo principal
  const pageContent = (
    <div className={classes.container}>
      {selectedItem && (
        <div className={classes.containerMap}>
          <MapView>
            <MapGeofence />
            {position && <MapPositions positions={[position]} />}
          </MapView>
          {position && (
            <MapCamera
              latitude={position.latitude}
              longitude={position.longitude}
            />
          )}
        </div>
      )}
      <div className={classes.containerMain}>
        <div className={classes.header}>
          {/* standardized export/print/agenda menu */}
          <ReportFilter
            onShow={handleShow}
            onExport={handleExportReport}
            onPrint={handlePrintReport}
            loading={loading}
            hasResults={hasResults}
            deviceType="single"
          >
            <Box className={classes.filterItem} sx={{ minWidth: 200 }}>
              <ColumnSelect
                columns={columns}
                setColumns={setColumns}
                columnsArray={columnsArray}
                rawValues={true}
                disabled={loading}
              />
            </Box>
          </ReportFilter>
          {/* Stats Cards */}
          {stats && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', px: 2, pb: 1, pt: 1, backgroundColor: tokens.surface.elevated, borderTop: `1px solid ${tokens.border.subtle}` }}>
              <Chip
                icon={<PlaceIcon sx={{ color: `${tokens.accent.primary} !important` }} />}
                label={`${stats.total} ${t('reportEvents')}`}
                sx={{ backgroundColor: tokens.accent.subtle, color: tokens.accent.primary, borderColor: tokens.accent.primary, fontWeight: 500 }}
                variant="outlined"
              />
              <Chip
                icon={<LoginIcon sx={{ color: `${tokens.semantic.success} !important` }} />}
                label={`${stats.enters} ${t('eventGeofenceEnter')}`}
                sx={{ borderColor: tokens.semantic.success, color: tokens.semantic.success }}
                variant="outlined"
              />
              <Chip
                icon={<LogoutIcon sx={{ color: `${tokens.semantic.warning} !important` }} />}
                label={`${stats.exits} ${t('eventGeofenceExit')}`}
                sx={{ borderColor: tokens.semantic.warning, color: tokens.semantic.warning }}
                variant="outlined"
              />
              {stats.stays > 0 && (
                <Chip
                  icon={<AccessTimeIcon sx={{ color: `${tokens.semantic.info} !important` }} />}
                  label={`${stats.stays} ${t('eventGeofenceStay')}`}
                  sx={{ borderColor: tokens.semantic.info, color: tokens.semantic.info }}
                  variant="outlined"
                />
              )}
              <Chip
                icon={<RepeatIcon sx={{ color: `${tokens.text.secondary} !important` }} />}
                label={`${stats.uniqueGeofences} ${t('sharedGeofences')}`}
                sx={{ borderColor: tokens.border.default, color: tokens.text.primary }}
                variant="outlined"
              />
            </Box>
          )}
        </div>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell className={classes.columnAction} />
              {columns.map((key) => (
                <TableCell key={key}>{t(columnsMap.get(key) || key)}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading ? (
              items.length > 0 ? (
                items
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item) => (
                    <TableRow key={item.id || Math.random()}>
                      <TableCell
                        className={classes.columnAction}
                        padding="none"
                      >
                        {item.positionId ? (
                          selectedItem === item ? (
                            <IconButton
                              size="small"
                              onClick={() => setSelectedItem(null)}
                            >
                              <GpsFixedIcon fontSize="small" />
                            </IconButton>
                          ) : (
                            <IconButton
                              size="small"
                              onClick={() => setSelectedItem(item)}
                            >
                              <LocationSearchingIcon fontSize="small" />
                            </IconButton>
                          )
                        ) : (
                          ""
                        )}
                      </TableCell>
                      {columns.map((key) => (
                        <TableCell key={key}>
                          {formatValue(item, key)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
              ) : (
                <TableRow style={{ height: 72 * 5 }}>
                  <TableCell align="center" colSpan={columns.length + 1}>
                    {t("sharedRowsEmpty")}
                  </TableCell>
                </TableRow>
              )
            ) : (
              <TableShimmer 
                columns={columns.length + 1} 
                startAction={true}
                endAction={false}
              />
            )}
            {/* Linhas vazias para paginação */}
            {emptyRows > 0 && items.length > 0 && (
              <TableRow style={{ height: 72 * emptyRows }}>
                <TableCell align="center" colSpan={columns.length + 1} />
              </TableRow>
            )}
          </TableBody>
        </Table>
        {/* Paginação - Mostrar apenas se tivermos dados */}
        {items.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[
              5,
              10,
              25,
              { label: t("sharedAll"), value: -1 },
            ]}
            component="div"
            count={items.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={t("sharedRowsPerPage")}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t("sharedOf")} ${count}`}
            SelectProps={{
              inputProps: { "aria-label": t("sharedRowsPerPage") },
              native: true,
            }}
          />
        )}
      </div>
    </div>
  );

  return (
    <PageLayout
      menu={<ReportsMenu />}
      breadcrumbs={["reportTitle", "sharedGeofences"]}
    >
      {pageContent}
    </PageLayout>
  );
};

export default GeofencesReportPage;
